import {customFieldConstants, supportedSourcesForCustomFields} from "_constants";
import _ from "lodash";
import {CustomFieldBuilder} from "./CustomFieldBuilder";

export function isCustomFieldSupported(msgSourceName) {
    return supportedSourcesForCustomFields.includes(_.toString(msgSourceName).toLowerCase());
}

export function buildCustomFields(customFieldList, conversationCustomFields) {
    const customFieldArr = [];

    customFieldList.forEach((customField) => {
        const customFieldObj = _.find(conversationCustomFields, (conversationCustomField) =>
            String(_.get(conversationCustomField, 'id')) === String(_.get(customField, 'sourceId'))
        );

        const builder = new CustomFieldBuilder();
        builder.setId(_.get(customField, 'id'));
        builder.setSourceId(_.get(customField, 'sourceId'));
        builder.setName(_.get(customField, 'name'));
        builder.setActive(_.get(customField, 'active'));

        if(customField?.customFieldOptions) {
            builder.setCustomFieldOptions(customField?.customFieldOptions);
        }

        if (customFieldObj) {
            const value = String(_.get(customFieldObj, 'value', ''));
            builder.setValue(value);
        }

        customFieldArr.push(builder.customField);
    });

    return customFieldArr;
}

export function buildCustomFieldSubmissionFormData(customFields, values) {
    return customFields.map(customField => {
        const fieldValue = values[customField?.name];
        return {
            id: customField?.sourceId,
            name: customField?.name,
            value: typeof fieldValue === "object" && fieldValue !== null ? fieldValue.value : fieldValue || '',
            required: true,
        };
    });
}

/**
 * @param customFields Array<Object>
 * @param customFieldsInput Array<Object>
 * @param shouldAddCustomFields {boolean}
 * @returns {{isValid: boolean, message: string}|{isValid: boolean}|{isValid: boolean, message: string}|{isValid: boolean}}
 */
export function validateCustomFieldsInput(customFields = [], customFieldsInput, shouldAddCustomFields) {
    if (!shouldAddCustomFields) return { isValid: true };

    if (_.isEmpty(customFieldsInput)) {
        const missingFields = customFields
            .filter(field => _.isEmpty(field.value))
            .map(field => field.name);

        return missingFields.length
            ? { isValid: false, message: `Missing required custom field(s) [${missingFields}]` }
            : { isValid: true };
    }

    const missingFields = customFields
        .filter(field => {
            const inputField = _.find(customFieldsInput, { id: field.sourceId }) || {};
            return (isEmpty(field.value) && isEmpty(inputField.value));
        })
        .map(field => field.name);

    return missingFields.length
        ? { isValid: false, message: `Missing required custom field(s) [${missingFields}]` }
        : { isValid: true };
}

export function extractRequiredCustomFields(customFieldsInput) {
    return customFieldsInput.filter(field => field.required);
}

/**
 * @param customField
 * @param msgDataSourceId
 * @returns {{sourceId, msgDataSource, name, active}}
 */
export function buildAddCustomFieldPayload({ customField = {}, msgDataSourceId }) {
    return {
        sourceId: customField.id,
        name: customField.name,
        active: customField.active,
        msgDataSource: msgDataSourceId,
        ...(customField.customFieldOptions && { customFieldOptions: customField.customFieldOptions }),
    };
}

export function filterCustomFields(customFields, macrosCustomField) {
    const validMacrosCustomField = Array.isArray(macrosCustomField) ? macrosCustomField : [];

    const excludedFieldIds = validMacrosCustomField
        .map(field => field?.customFields?.map(cf => cf?.custom_field_id))
        .flat()
        .filter(id => id != null)
        .map(id => String(id));

    const validCustomFields = Array.isArray(customFields) ? customFields : [];

    return validCustomFields.filter(field => field?.sourceId && !excludedFieldIds.includes(String(field.sourceId)));
}

export function extractZendeskCustomFieldsFromMacros(macroId, macros) {
    if (isEmpty(macros)) {
        return {};
    }
    const filteredMacros = _.filter(macros, macro => macro.id == macroId);
    if (_.isEmpty(filteredMacros)) {
        return {};
    }

    return filteredMacros.map(macro => ({
        id: _.get(macro, 'id'),
        name: _.get(macro, 'name'),
        customFields: _(macro.actions || [])
            .filter(action => _.get(action, 'field', '').startsWith('custom_fields_'))
            .map(action => ({
                custom_field_id: _.get(action, 'field', '').split('_').pop(), // Keep as string
                value: _.get(action, 'value')
            }))
            .value()
    }));
}

export function extractGorgiasCustomFieldsFromMacros(macroId, macros) {
    if (_.isEmpty(macros)) {
        return {};
    }
    const filteredMacros = _.filter(macros, macro => macro.id == macroId);
    if (_.isEmpty(filteredMacros)) {
        return {};
    }

    return filteredMacros.map(macro => ({
        id: _.get(macro, 'id'),
        name: _.get(macro, 'name'),
        customFields: _(macro.actions || [])
            .filter(action => _.get(action, 'field') === 'setCustomFieldValue')
            .map(action => ({
                custom_field_id: _.get(action, 'arguments.custom_field_id'),
                value: _.get(action, 'arguments.value')
            }))
            .value()
    }));
}

export const customFieldActionsCreator = {
    settingsView: {
        request: customFieldConstants.LIST_CUSTOM_FIELDS_REQUEST,
        success: customFieldConstants.LIST_CUSTOM_FIELDS_SUCCESS,
        failure: customFieldConstants.LIST_CUSTOM_FIELDS_FAILURE
    },
    messageView: {
        request: customFieldConstants.LIST_CUSTOM_FIELDS_FOR_MESSAGE_REQUEST,
        success: customFieldConstants.LIST_CUSTOM_FIELDS_FOR_MESSAGE_SUCCESS,
        failure: customFieldConstants.LIST_CUSTOM_FIELDS_FOR_MESSAGE_FAILURE
    }
}

function isEmpty(value) {
    return (
        value === undefined ||
        (Array.isArray(value) && value.every(item => item === undefined))
    );
}







